import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderMain from "./Components/HeaderMain";
import ProfilePic from "./images/dp.png";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BASE_URL;

const UserSearchResults = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const { user } = useAuth();
  const searchQuery = searchParams.get("q");

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!searchQuery) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          `${API_URL}/api/common/search-users?term=${encodeURIComponent(
            searchQuery
          )}`
        );

        if (response.ok) {
          const data = await response.json();
          setSearchResults(data);
        } else {
          throw new Error("Failed to fetch search results");
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching search results:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [searchQuery]);

  const handleUserClick = (userId) => {
    navigate(`/player-profile/${userId}`);
  };

  if (loading) {
    return (
      <div className="App">
        <HeaderMain pageType="user-profile" />
        <div className="max-w-6xl mx-auto px-5 min-h-screen">
          <div className="text-center py-10 text-gray-600 text-xl">
            Loading search results...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <HeaderMain pageType="user-profile" />
        <div className="max-w-6xl mx-auto px-5 min-h-screen">
          <div className="text-center py-10 text-red-600 bg-red-50 border border-red-200 rounded-lg my-5 text-xl">
            Error: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <HeaderMain pageType="user-search-profile" />
      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-5 min-h-screen py-4 sm:py-6">
        <div className="mb-4 sm:mb-6 md:mb-8 text-center">
          <h2 className="text-gray-800 mb-1 sm:mb-2 text-xl sm:text-2xl md:text-3xl font-semibold px-2">
            Search Results for "{searchQuery}"
          </h2>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg">
            {searchResults.length} user(s) found
          </p>
        </div>

        {searchResults.length === 0 ? (
          <div className="text-center py-10 sm:py-12 md:py-16 px-4 sm:px-5 text-gray-600">
            <h3 className="mb-2 sm:mb-4 text-gray-800 text-lg sm:text-xl font-medium">
              No users found
            </h3>
            <p className="text-sm sm:text-base">Try searching with different keywords or check your spelling.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5 mt-3 sm:mt-4 md:mt-5">
            {searchResults.map((user) => (
              <div
                key={user.id}
                className="bg-white border border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 cursor-pointer transition-all duration-300 shadow-sm hover:-translate-y-1 hover:shadow-lg hover:border-blue-500"
                onClick={() => handleUserClick(user.id)}
              >
                <div className="flex justify-center mb-2 sm:mb-3 md:mb-4">
                  <img
                    src={
                      user.picture
                        ? `data:image/jpeg;base64,${user.picture}`
                        : ProfilePic
                    }
                    alt={user.display_name}
                    className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full object-cover border-2 sm:border-3 md:border-4 border-gray-100"
                  />
                </div>
                <div className="text-center mb-2 sm:mb-3 md:mb-4">
                  <h3 className="text-sm sm:text-base md:text-xl font-semibold text-gray-800 mb-0.5 sm:mb-1 truncate">
                    {user.display_name}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-0.5 sm:mb-1 truncate">
                    {user.full_name}
                  </p>
                  {user.user_name && (
                    <p className="text-xs sm:text-sm text-blue-600 font-medium truncate">
                      @{user.user_name}
                    </p>
                  )}
                </div>
                <div className="flex justify-center">
                  <button className="bg-blue-600 text-white border-none py-1.5 sm:py-2 px-3 sm:px-4 md:px-5 rounded-md cursor-pointer text-xs sm:text-sm font-medium transition-colors duration-300 hover:bg-blue-700 w-full sm:w-auto">
                    View Profile
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSearchResults;
